<!-- src/components/dashboard/tasks/TaskList.vue
  FT-076: переиспользуемый список задач.
  - Общий режим (без taskableType): фильтры мои/все + статус + просроченные + поиск.
  - Entity-режим (taskableType+taskableId): scoped-список + compose с предзаполненным владельцем. -->
<template>
  <div>
    <!-- Фильтры (только общий режим) -->
    <div v-if="!scoped" class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
      <v-btn-toggle v-model="filterAssignee" dense mandatory color="primary" @change="reload">
        <v-btn small value="me">{{ $t('tasks.filter_mine') }}</v-btn>
        <v-btn small value="all">{{ $t('tasks.filter_all') }}</v-btn>
      </v-btn-toggle>
      <v-select v-model="filterStatus" :items="statusOptions" dense hide-details outlined
                style="max-width: 160px" @change="reload" />
      <v-checkbox v-model="filterOverdue" :label="$t('tasks.filter_overdue')" dense hide-details
                  class="mt-0 pt-0" @change="reload" />
      <v-spacer />
      <v-text-field v-model="search" :placeholder="$t('tasks.search')" dense hide-details outlined
                    clearable prepend-inner-icon="mdi-magnify" style="max-width: 220px"
                    @input="debouncedReload" />
    </div>

    <!-- Compose -->
    <div class="d-flex align-center mb-2">
      <div v-if="scoped" class="text-subtitle-2">{{ $t('tasks.title') }}<span v-if="items.length" class="grey--text"> ({{ items.length }})</span></div>
      <v-spacer v-if="scoped" />
      <v-btn text small color="primary" @click="composeOpen = !composeOpen">
        <v-icon small left>{{ composeOpen ? 'mdi-chevron-up' : 'mdi-plus' }}</v-icon>{{ $t('tasks.add') }}
      </v-btn>
    </div>

    <div v-if="composeOpen" class="mb-3">
      <v-form ref="composeForm" @submit.prevent>
        <v-text-field v-model="draftTitle" :placeholder="$t('tasks.title_placeholder')" dense
                      hide-details="auto" :rules="titleRules" class="mb-3" />
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <v-select v-model="draftPriority" :items="priorityOptions" dense hide-details style="max-width: 120px" />
          <v-select v-model="draftAssignee" :items="assigneeOptions" :label="$t('tasks.assignee')" dense hide-details clearable style="max-width: 170px" />
          <v-spacer />
          <!-- Срок -->
          <component :is="pickerWrapper" v-model="dueMenu" v-bind="pickerWrapperProps">
            <template #activator="{ on, attrs }">
              <v-btn icon :color="draftDue ? 'primary' : ''" v-bind="attrs" v-on="on" :title="$t('tasks.due')"><v-icon>mdi-calendar</v-icon></v-btn>
            </template>
            <v-card>
              <v-date-picker v-model="draftDue" :locale="$i18n.locale" first-day-of-week="1" no-title scrollable @input="dueMenu = false" />
              <div class="d-flex justify-end px-2 py-1"><v-btn icon x-small @click="draftDue = null; dueMenu = false"><v-icon x-small>mdi-calendar-remove</v-icon></v-btn></div>
            </v-card>
          </component>
          <!-- Повтор -->
          <v-menu v-model="recurringMenu" offset-y min-width="200px">
            <template #activator="{ on, attrs }">
              <v-btn icon :color="draftRecurringInterval ? 'primary' : ''" v-bind="attrs" v-on="on" :title="$t('tasks.recurring_interval')"><v-icon>mdi-repeat</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="opt in recurringIntervalOptions" :key="String(opt.value)"
                           :input-value="draftRecurringInterval === opt.value" @click="draftRecurringInterval = opt.value">
                <v-list-item-title>{{ opt.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <!-- Напоминание -->
          <component :is="pickerWrapper" v-model="remindMenu" v-bind="pickerWrapperProps">
            <template #activator="{ on, attrs }">
              <v-btn icon :color="draftRemindDate ? 'primary' : ''" v-bind="attrs" v-on="on" :title="$t('tasks.remind')"><v-icon>{{ draftRemindDate ? 'mdi-bell' : 'mdi-bell-outline' }}</v-icon></v-btn>
            </template>
            <v-card>
              <v-tabs v-model="remindTab" grow height="40">
                <v-tab>{{ $t('tasks.remind') }}</v-tab>
                <v-tab :disabled="!draftRemindDate">{{ draftRemindTime || '--:--' }}</v-tab>
              </v-tabs>
              <v-date-picker v-if="remindTab === 0" v-model="draftRemindDate" :locale="$i18n.locale" first-day-of-week="1" no-title scrollable @input="remindTab = 1" />
              <v-time-picker v-else v-model="draftRemindTime" format="24hr" scrollable @click:minute="remindMenu = false" />
              <div class="d-flex justify-end px-2 py-1"><v-btn icon x-small @click="clearRemind"><v-icon x-small>mdi-bell-off-outline</v-icon></v-btn></div>
            </v-card>
          </component>
          <v-btn fab small color="primary" :loading="creating" :disabled="addDisabled" :title="$t('tasks.add')" @click="add"><v-icon>mdi-plus</v-icon></v-btn>
        </div>
      </v-form>
    </div>

    <!-- Список -->
    <div v-if="loading" class="text-center py-4"><v-progress-circular indeterminate size="24" /></div>
    <div v-else-if="!items.length" class="grey--text text-center py-4">{{ $t('tasks.empty') }}</div>
    <div v-else>
      <div v-for="t in items" :key="t.id" class="d-flex align-start mb-2">
        <v-icon class="mr-2 mt-1" :title="t.completed_at ? $t('tasks.reopen') : $t('tasks.complete')" @click="toggle(t)">{{ t.completed_at ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
        <div class="flex-grow-1">
          <div :class="{ 'text-decoration-line-through grey--text': t.completed_at }">{{ t.title }}</div>
          <div class="d-flex align-center flex-wrap mt-1" style="gap: 4px 10px">
            <v-chip x-small label :color="priorityColor(t.priority)">{{ $t('tasks.priority_' + t.priority) }}</v-chip>
            <v-chip v-if="!scoped && t.taskable" x-small label :color="taskableColor(t.taskable.type)">
              <v-icon x-small left>{{ taskableIcon(t.taskable.type) }}</v-icon>{{ t.taskable.label }}
            </v-chip>
            <v-chip v-if="t.is_recurring" x-small label color="info"><v-icon x-small left>mdi-repeat</v-icon>{{ recurringLabel(t) }}</v-chip>
            <span v-if="t.due_at" class="caption" :class="{ 'error--text': isOverdue(t) }"><v-icon x-small :color="isOverdue(t) ? 'error' : undefined">mdi-calendar</v-icon> {{ formatDate(t.due_at) }}</span>
            <span v-if="t.remind_at" class="caption grey--text"><v-icon x-small>mdi-bell-outline</v-icon> {{ formatDateTime(t.remind_at) }}</span>
            <span class="caption grey--text"><v-icon x-small>mdi-account-outline</v-icon> {{ assigneeName(t.assigned_to_id) }}</span>
          </div>
        </div>
        <v-btn v-if="t.is_recurring && canModify(t)" icon small class="align-self-center" :title="$t('tasks.stop_recurrence')" @click="askStop(t)"><v-icon small>mdi-repeat-off</v-icon></v-btn>
        <v-btn v-if="canModify(t)" icon small class="align-self-center" :title="$t('tasks.delete')" @click="remove(t)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
      </div>
    </div>

    <v-dialog v-model="stopDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ $t('tasks.stop_recurrence') }}</v-card-title>
        <v-card-text>{{ $t('tasks.stop_recurrence_confirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="stopping" @click="stopDialog = false">{{ $t('tasks.cancel') }}</v-btn>
          <v-btn color="warning" text :loading="stopping" @click="confirmStop">{{ $t('tasks.stop_recurrence') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackColor" timeout="4000">{{ snackText }}</v-snackbar>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { parseCrmError } from "@/store/modules/tasks/api";
import mobilePickerMixin from "@/mixins/mobilePickerMixin";

let searchTimer = null;

export default {
  name: "TaskList",
  mixins: [mobilePickerMixin],
  props: {
    taskableType: { type: String, default: null },
    taskableId: { type: [String, Number], default: null },
  },
  data: () => ({
    composeOpen: false,
    draftTitle: "", draftPriority: "normal", draftAssignee: null, draftDue: null,
    draftRecurringInterval: null, draftRemindDate: null, draftRemindTime: null,
    dueMenu: false, recurringMenu: false, remindMenu: false, remindTab: 0,
    filterAssignee: "me", filterStatus: "open", filterOverdue: false, search: "",
    stopDialog: false, stopTarget: null, stopping: false,
    snackbar: false, snackText: "", snackColor: "error",
  }),
  computed: {
    ...mapState("tasks", { items: (s) => s.items, loading: (s) => s.loading, creating: (s) => s.creating, users: (s) => s.users }),
    ...mapGetters(["currentUserId", "isStaff", "getCompany"]),
    companyTz() { const c = this.getCompany && this.getCompany.company; return (c && c.time_zone) || undefined; },
    scoped() { return !!this.taskableType; },
    statusOptions() { return ["open", "done", "all"].map((v) => ({ value: v, text: this.$t("tasks.status_" + v) })); },
    priorityOptions() { return ["low", "normal", "high"].map((p) => ({ value: p, text: this.$t("tasks.priority_" + p) })); },
    recurringIntervalOptions() {
      return [{ value: null, text: this.$t("tasks.recurring_interval_none") }]
        .concat(["daily", "weekly", "monthly"].map((i) => ({ value: i, text: this.$t("tasks.recurring_interval_" + i) })));
    },
    assigneeOptions() { return this.users.map((u) => ({ value: u.id, text: u.name })); },
    addDisabled() { return this.creating || !this.draftTitle.trim(); },
    titleRules() { return [(v) => !!(v && v.trim()) || this.$t("tasks.title_required")]; },
    remindPayload() { return this.draftRemindDate ? `${this.draftRemindDate}T${this.draftRemindTime || "09:00"}:00` : null; },
  },
  created() {
    // Дефолт «исполнитель = я» ставим синхронно, не внутри .then: если запрос
    // юзеров падал (403 у роли user до fix tasks/users), задача создавалась без
    // исполнителя. Зеркалит TaskFormDialog.
    if (this.draftAssignee == null) this.draftAssignee = this.currentUserId;
    // Задачи закрыты для ролей вне STAFF_ROLES (ensure_staff! на бэкенде).
    // TaskCardSection монтирует нас через v-show, поэтому без этого гейта
    // partner/guest/agent били парой 403 на каждое открытие карточки.
    if (!this.isStaff) return;
    this.fetchTaskUsers().catch(() => {});
    this.reload();
  },
  methods: {
    ...mapActions("tasks", ["fetchTasks", "createTask", "completeTask", "reopenTask", "stopTaskRecurrence", "deleteTask", "fetchTaskUsers"]),
    queryParams() {
      if (this.scoped) return { assignee: "all", status: "all", taskable_type: this.taskableType, taskable_id: this.taskableId, per_page: 200 };
      return { assignee: this.filterAssignee, status: this.filterStatus, overdue: this.filterOverdue ? "true" : undefined, search: this.search || undefined, per_page: 100 };
    },
    reload() { this.fetchTasks(this.queryParams()).catch((e) => this.notifyErr(e)); },
    debouncedReload() { clearTimeout(searchTimer); searchTimer = setTimeout(() => this.reload(), 350); },
    notifyErr(e) { this.snackText = parseCrmError(e).fallback; this.snackColor = "error"; this.snackbar = true; },
    priorityColor(p) { return p === "high" ? "error" : p === "low" ? "grey" : "primary"; },
    taskableIcon(t) { return { Lead: "mdi-account-star", Booking: "mdi-calendar-check", Car: "mdi-car", Client: "mdi-account" }[t] || "mdi-link"; },
    taskableColor(t) { return { Lead: "purple", Booking: "teal", Car: "indigo", Client: "blue-grey" }[t] || "grey"; },
    recurringLabel(t) { return this.$t("tasks.recurring_interval_" + t.recurring_interval); },
    formatDate(d) { return d ? new Date(d).toLocaleDateString(this.$i18n.locale, { timeZone: this.companyTz }) : ""; },
    formatDateTime(d) { return d ? new Date(d).toLocaleString(this.$i18n.locale, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: this.companyTz }) : ""; },
    clearRemind() { this.draftRemindDate = null; this.draftRemindTime = null; this.remindTab = 0; },
    isOverdue(t) { return !t.completed_at && t.due_at && new Date(t.due_at) < new Date(); },
    assigneeName(id) { const u = id && this.users.find((x) => x.id === id); return (u && u.name) || this.$t("tasks.unassigned"); },
    canModify(t) { return t.created_by_id === this.currentUserId || this.isAdminPlus; },
    async add() {
      if (this.addDisabled) return;
      const payload = {
        title: this.draftTitle.trim(), priority: this.draftPriority,
        recurring_interval: this.draftRecurringInterval || null,
        due_at: this.draftDue ? `${this.draftDue}T23:59:00` : null,
        remind_at: this.remindPayload,
        assigned_to_id: this.draftAssignee || undefined,
      };
      if (this.scoped) { payload.taskable_type = this.taskableType; payload.taskable_id = this.taskableId; }
      try {
        await this.createTask(payload);
        this.draftTitle = ""; this.draftDue = null; this.draftPriority = "normal"; this.draftRecurringInterval = null;
        this.draftRemindDate = null; this.draftRemindTime = null; this.remindTab = 0; this.draftAssignee = this.currentUserId;
        this.composeOpen = false;
        this.$emit("changed", this.items.length);
        this.$nextTick(() => { if (this.$refs.composeForm) this.$refs.composeForm.resetValidation(); });
      } catch (e) { this.notifyErr(e); }
    },
    async toggle(t) {
      try {
        if (t.completed_at) await this.reopenTask(t.id);
        else { await this.completeTask(t.id); if (t.is_recurring) this.reload(); }
      } catch (e) { this.notifyErr(e); }
    },
    async remove(t) {
      try { await this.deleteTask(t.id); this.$emit("changed", this.items.length); }
      catch (e) { this.notifyErr(e); }
    },
    askStop(t) { this.stopTarget = t; this.stopDialog = true; },
    async confirmStop() {
      if (!this.stopTarget) return;
      this.stopping = true;
      try { await this.stopTaskRecurrence(this.stopTarget.id); this.stopDialog = false; this.stopTarget = null; }
      catch (e) { this.notifyErr(e); }
      finally { this.stopping = false; }
    },
  },
};
</script>
